import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Redirect, useLocation } from "react-router-dom";
import { shallowEqual, useSelector } from "react-redux";
import { RootState } from "typedefs";
import { isAcademyOnboardingCoach } from "app/utils/subscription";
import Navbar from "app/components/Navbar";
import SetupChecklist from "app/components/AcademySetup/SetupChecklist";
import {
  AcademySetupLocationState,
  completedCount,
} from "app/components/AcademySetup/items";
import useAcademySetupItems from "app/utils/hooks/useAcademySetupItems";
import { trackSetupPageViewed } from "app/utils/analytics-helpers";
import BookDemoBanner from "./_components/BookDemoBanner";
import FaqList from "./_components/FaqList";
import styles from "./styles.module.scss";

// Once per session; sessionStorage so a reload doesn't count as a second view.
const VIEWED_KEY = "academy-setup-viewed";

const AcademySetup = () => {
  const { t } = useTranslation();
  const location = useLocation<AcademySetupLocationState | undefined>();
  const self = useSelector(
    (state: RootState) => state.user.self,
    shallowEqual,
  );
  const { items, loaded } = useAcademySetupItems();
  const entryPoint = location.state?.setupEntryPoint;
  const eligible = isAcademyOnboardingCoach(self);

  useEffect(() => {
    // Waits for the fetch so items_complete_count is the API's number, not 0.
    // Same gate as the redirect below: a visit that redirects (a downgraded
    // coach whose slice is still cached) is not a view, and must not spend
    // the once-per-session key on one.
    if (!eligible || !loaded || sessionStorage.getItem(VIEWED_KEY)) {
      return;
    }
    sessionStorage.setItem(VIEWED_KEY, "1");
    trackSetupPageViewed(completedCount(items), entryPoint || "direct");
  }, [eligible, loaded, items, entryPoint]);

  // PrivateRoute renders its page while the account is still loading (a
  // hard refresh), so an absent user is "not yet known", not ineligible.
  if (!self) {
    return null;
  }
  // Same gate as SetupChecklist: anyone else would get the heading, intro,
  // FAQ and demo banner with no checklist between them.
  if (!eligible) {
    return <Redirect to="/" />;
  }

  return (
    <div className={styles.container}>
      <Navbar
        label={t("Academy setup")}
        hasSearch={false}
        hasNotifications={false}
      />
      <p className={styles.intro}>
        {t(
          "Let's get your account fully configured so you can start accepting bookings and managing athletes right away.",
        )}
      </p>
      <SetupChecklist />
      <section className={styles.articles}>
        <h2 className={styles.articles_title}>
          {t("Helpful articles for your setup")}
        </h2>
        <p className={styles.articles_text}>
          {t(
            "Having trouble or questions? Find helpful articles about all main CoachNow features here. Still need help? Feel free to book a demo.",
          )}
        </p>
        <BookDemoBanner />
        <FaqList />
      </section>
    </div>
  );
};

export default AcademySetup;
